"use client"

/**
 * "Question search" block for the profile menu — the switch between the two
 * answers the question bank's Search hub can give (`use-question-search-mode`).
 *
 * Same placement argument as `HomeLayoutMenu`: a standing preference about a
 * surface belongs with theme and layout, not in the surface's own header, where
 * it would spend the most valuable row on the page on a setting most people set
 * once. Rendered in both profile menus so the choice is reachable from the hub
 * as well as from the utility bar.
 */

import {
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import {
  QUESTION_SEARCH_MODES,
  setQuestionSearchMode,
  useQuestionSearchMode,
  type QuestionSearchMode,
} from "@/hooks/use-question-search-mode"

export function QuestionSearchModeMenu() {
  const mode = useQuestionSearchMode()
  const active = QUESTION_SEARCH_MODES.find((option) => option.id === mode)

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <i className="fa-light fa-magnifying-glass" aria-hidden="true" />
        Question search
        {/* Trailing current value, the way the theme and Home layout rows read,
            so the setting is legible without opening the submenu. */}
        <span className="ms-auto pe-1 text-xs text-muted-foreground">
          {active?.label}
        </span>
      </DropdownMenuSubTrigger>
      {/* No `DropdownMenuPortal` — see the note in `HomeLayoutMenu`: portalled
          sub-content lands under the dismissable layer and stops taking clicks. */}
      <DropdownMenuSubContent className="w-64">
        <DropdownMenuRadioGroup
          value={mode}
          onValueChange={(value) =>
            setQuestionSearchMode(value as QuestionSearchMode)
          }
        >
          {QUESTION_SEARCH_MODES.map((option) => (
            <DropdownMenuRadioItem
              key={option.id}
              value={option.id}
              className="items-start gap-2 py-2"
            >
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">{option.label}</span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {option.description}
                </span>
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
